import React, { useMemo, useState, useRef, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Alert,
  Button,
  Grid,
  TextField,
  MenuItem,
  Snackbar,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CommonModal from "components/common/CommonModal";
import { useSessionClass } from "contexts/SessionClassContext";
import { getMarksheetByStudentId, useMarkSheets } from "hooks/useMarkSheets";
import { useAuth } from "hooks/useAuth";
import { useSchools } from "hooks/useSchools";
import { useStudents } from "hooks/useStudents";
import { useClasses } from "hooks/useClasses";
import { useTranslation } from "react-i18next";
import TcListTemplate from "components/templates/TcListTemplate";
import TcTemplate from "components/templates/TcTemplate";
import { Description } from "@mui/icons-material";
import EnhancedTable from "components/common/EnhancedTable";
import { useTdData } from "hooks/useTdData";
import { useQueryClient } from "@tanstack/react-query";
import { getStudentData } from "utils/storage";
import { useError } from "contexts/ErrorContext";
import { getClassRank } from "components/enum/ClassOptions";

const TcManagement: React.FC = () => {
  const { t } = useTranslation();
  const { selectedClass, selectedSession } = useSessionClass();
  const { user, isStudent } = useAuth();
  const { useSchoolById } = useSchools();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useError();
  const schoolId = user?.schoolId ?? undefined;
  const { data: currentSchool } = useSchoolById(schoolId);
  const { classesQuery } = useClasses(schoolId);
  const [marksheetType, setMarksheetType] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedExamType, setSelectedExamType] = useState("");
  const [tcDisplayDialogOpen, setTcDisplayDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const tcTemplateRef = useRef<HTMLDivElement>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editFormValues, setEditFormValues] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [studentTcData, setStudentTcData] = useState<any>(null);
  const [loadingStudentTc, setLoadingStudentTc] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "warning" | "info";
  }>({
    open: false,
    message: "",
    severity: "info",
  });
  // Removed inline error banner per request

  // Clear TC data cache when school changes to prevent showing data from previous school
  useEffect(() => {
    if (user?.schoolId) {
      // Remove all cached TC data from React Query to ensure fresh fetch
      queryClient.removeQueries({ queryKey: ["tcdata"] });
      queryClient.removeQueries({ queryKey: ["tclist"] });
      
      // Also clear localStorage cache from old schools (clean up old entries)
      try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('tcdata:') && !key.startsWith(`tcdata:${user.schoolId}:`)) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
      } catch (err) {
      }
    }
  }, [user?.schoolId, queryClient]);

  // Set default marksheet type based on class
  React.useEffect(() => {
    if (selectedClass && typeof selectedClass.name === "string") {
      const className = selectedClass.name.trim();
      if (className.startsWith("10th") || className.startsWith("12th")) {
        setMarksheetType("BOARD");
      } else {
        setMarksheetType("ANNUAL");
      }
    } else {
      setMarksheetType(""); // or "ANNUAL" as default if you prefer
    }
  }, [selectedClass]);

  // Prepare the payload for tcList
  const tcListPayload = useMemo(
    () => {
      const rawClassName = String(selectedClass?.name ?? "").trim();
      const isAllClass =
        (selectedClass?.id != null && Number(selectedClass.id) < 0) ||
        /\bALL\b$/i.test(rawClassName.replace(/-/g, " "));

      const classNameForApi = isAllClass
        ? (() => {
            if (/-ALL$/i.test(rawClassName)) return rawClassName.replace(/-all$/i, "-ALL");
            if (/\s+ALL$/i.test(rawClassName)) return rawClassName.replace(/\s+ALL$/i, "-ALL");
            return rawClassName ? `${rawClassName}-ALL` : "ALL";
          })()
        : rawClassName;

      const payload = {
        schoolId: user?.schoolId ?? 0,
        classId: isAllClass ? null : (selectedClass?.id ?? 0),
        className: classNameForApi,
        sessionId: selectedSession?.id ?? 0,
        marksheetType,
      };
      return payload;
    },
    [user, selectedClass, selectedSession, marksheetType]
  );

  // Check if user is a student
  const isStudentUser = isStudent();

  // Fetch tcList - disable marksheetsQuery for students
  const { useTcList, marksheetsQuery } = useMarkSheets(
    false,
    isStudentUser,
    selectedClass?.id,
    selectedSession?.id
  );
  const { addTcDataMutation, useTcData, getTcByStudentCode } = useTdData();
  
  // For students: Fetch student data using mobile number API to get studentCode
  const { useStudentByMobileNo } = useStudents();
  const studentMobile = isStudentUser ? (user?.email || "") : "";
  const isValidMobile = studentMobile && !studentMobile.includes('@') && /^\d{10}$/.test(studentMobile.replace(/\D/g, ''));
  
  const { 
    data: studentDataByMobile,
    isLoading: loadingStudentByMobile 
  } = useStudentByMobileNo(
    isStudentUser && isValidMobile ? studentMobile : undefined,
    isStudentUser && isValidMobile ? selectedSession?.id : undefined,
    isStudentUser && isValidMobile ? selectedClass?.id : undefined
  );

  // Extract studentCode from the fetched student data (prioritize API response over storage)
  const studentCode = studentDataByMobile?.studentCode || (isStudentUser ? getStudentData()?.studentCode : null);
  
  // Use studentDataByMobile if available, otherwise fall back to storage
  const studentData = studentDataByMobile || (isStudentUser ? getStudentData() : null);
  
  // Fetch TC data for the selected class and session
  const tcDataClassId = selectedClass?.id != null && Number(selectedClass.id) > 0 ? Number(selectedClass.id) : undefined;
  const {
    data: tcDataList,
    isLoading: tcDataLoading,
    refetch: refetchTcData,
  } = useTcData(
    selectedSession?.id,
    tcDataClassId,
    user?.schoolId ?? undefined,
    Number(selectedSession?.id ?? 0) > 0 && Number(tcDataClassId ?? 0) > 0
  );
  
  // Log TC data when it changes for debugging
  React.useEffect(() => {
    if (tcDataList && tcDataList.length > 0) {
    }
  }, [tcDataList]);
  const {
    data: tcList,
    isLoading,
    error,
    refetch: refetchTcList,
  } = useTcList(
    tcListPayload,
    !!user?.schoolId && !!selectedClass && !!selectedSession && !!marksheetType
  );

  // Keep selectedStudent reference updated if tcList refreshes
  useEffect(() => {
    if (!selectedStudent || !tcList || tcList.length === 0) return;
    const refreshed = tcList.find((s: any) => s.studentId === selectedStudent.studentId);
    if (refreshed) setSelectedStudent(refreshed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tcList]);

  // Refetch tcList and tcData periodically and when component comes into view to catch updates
  useEffect(() => {
    if (!user?.schoolId || !selectedClass || !selectedSession || !marksheetType) return;
    
    // Refetch every 30 seconds to catch any updates
    const interval = setInterval(() => {
      refetchTcList();
      refetchTcData();
    }, 30000);
    
    // Refetch when window gains focus (user might have updated student in another tab/window)
    const handleFocus = () => {
      refetchTcList();
      refetchTcData();
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.schoolId, selectedClass?.id, selectedSession?.id, marksheetType]);

  // Cleanup: Remove TC data cache when component unmounts to prevent data leakage
  useEffect(() => {
    return () => {
      queryClient.removeQueries({ queryKey: ["tcdata"] });
    };
  }, [queryClient]);

  const className = selectedClass?.name?.toLowerCase() || "";
  const isBoardClass =
    className.startsWith("10th") || className.startsWith("12th");

  const handleOpenDialog = () => {
    setSelectedExamType(marksheetType);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  // Open TC template for a specific student (from table action)
  const handleOpenTcForStudent = (tcStudent: any | null, marksheetId?: number) => {
    if (!tcStudent) {
      alert("TC data not available for this student.");
      return;
    }
    // Refetch TC data before opening dialog to ensure fresh data
    refetchTcData();
    // Store marksheetId with the student for later matching
    setSelectedStudent({ ...tcStudent, __marksheetId: marksheetId });
    setTcDisplayDialogOpen(true);
  };

  const handleCloseTcDisplayDialog = () => {
    setTcDisplayDialogOpen(false);
    setSelectedStudent(null);
    if (isStudentUser) {
      setStudentTcData(null);
    }
  };

  // Handle Get TC for student users
  const handleGetTcForStudent = async () => {
    if (!studentCode || !selectedSession?.id || !selectedClass?.id) {
      setSnackbar({
        open: true,
        message: t("please_select_a_class_and_session_to_view_your_tc"),
        severity: "warning",
      });
      return;
    }
    
    try {
      setLoadingStudentTc(true);
      const tcData = await getTcByStudentCode(
        selectedSession.id,
        selectedClass.id,
        studentCode
      );
      setStudentTcData(tcData);
      // Create a student object for the template
      const currentStudentData = studentDataByMobile || studentData;
      const studentObj = {
        studentId: currentStudentData?.id,
        studentName: currentStudentData?.studentName || user?.username || "Student",
        ...currentStudentData,
      };
      setSelectedStudent(studentObj);
      setTcDisplayDialogOpen(true);
    } catch (error: any) {
      let errorMessage = t("failed_to_fetch_tc_data");
      
      // Check if TC is not created (404 or specific error message)
      if (error?.response?.status === 404 || 
          error?.response?.data?.message?.toLowerCase().includes("not found") ||
          error?.response?.data?.message?.toLowerCase().includes("not created")) {
        errorMessage = t("tc_not_created_yet");
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setLoadingStudentTc(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // TC list table should be driven from tclist API response.

  const tableColumns = useMemo(() => ([
    { id: "studentName", label: t("student_name"), minWidth: 160 },
    { id: "result", label: t("result"), minWidth: 100 },
    { id: "className", label: t("class"), minWidth: 100 },
    { id: "sessionYear", label: t("session_year"), minWidth: 120 },
  ]), [t]);

  const formattedRows = useMemo(() => {
    const list = Array.isArray(tcList) ? tcList : [];
    return list.map((s: any) => {
      const studentId = s.studentId ?? s.student?.id ?? undefined;
      const marksheetId = s.marksheetId ?? s.marksheetID ?? s.marksheet?.id ?? undefined;
      const matchingTcData =
        marksheetId && Array.isArray(tcDataList)
          ? tcDataList.find((tc: any) => tc.marksheet && Number(tc.marksheet.id) === Number(marksheetId))
          : null;

      return {
        studentId,
        marksheetId,
        studentName: s.studentName || t("unknown_student"),
        result: s.result || t("not_available"),
        className: s.className || selectedClass?.name || "",
        sessionYear: s.session || s.sessionYear || selectedSession?.name || "",
        __tcStudent: s,
        __tcData: matchingTcData,
      };
    });
  }, [tcList, tcDataList, t, selectedClass?.name, selectedSession?.name]);

  // Load cached TC data for student from localStorage (school-specific to prevent cross-school data leakage)
  const getCachedTcData = () => null;

  const cacheTcData = () => {};

  const typeMatchesTcSelection = (mType: unknown, want: string): boolean => {
    const mt = String(mType ?? "").trim();
    const wt = String(want ?? "").trim();
    if (!wt) return true;
    if (mt === wt) return true;
    if ((wt === "ANNUAL" && mt === "FINAL") || (wt === "FINAL" && mt === "ANNUAL")) return true;
    if (wt === "BOARD" && (mt === "BOARD" || mt === "PRE_BOARD")) return true;
    return false;
  };

  // Resolve marksheet id from already loaded marksheets
  const resolveMarksheetId = (studentId?: number) => {
    if (!studentId) return undefined;
    const ms = (marksheetsQuery.data || []).find((m: any) =>
      (m.student?.id === studentId || m.studentId === studentId) &&
      (!!selectedClass?.id ? m.classId === selectedClass.id : true) &&
      (!!selectedSession?.id ? m.sessionId === selectedSession.id : true) &&
      (!!marksheetType ? typeMatchesTcSelection(m.marksheetType, marksheetType) : true)
    );
    return (ms as any)?.id;
  };

  const resolveMarksheetIdFromServer = async (studentId?: number): Promise<number | undefined> => {
    const sid = Number(studentId);
    if (!Number.isFinite(sid) || sid <= 0) return undefined;

    const rows = await getMarksheetByStudentId(sid);
    const list = (Array.isArray(rows) ? rows : []).filter(Boolean) as any[];

    const classId = selectedClass?.id != null ? Number(selectedClass.id) : undefined;
    const sessionId = selectedSession?.id != null ? Number(selectedSession.id) : undefined;
    const sessionName = String(selectedSession?.name || "").trim();
    const wantType = String(marksheetType || "").trim();

    const rowStudentId = (m: any) => Number(m?.student?.id ?? m?.studentId ?? 0);

    const matchesStudent = (m: any) => {
      const rid = rowStudentId(m);
      if (rid > 0) return rid === sid;
      // Endpoint is scoped by studentId; tolerate rows missing nested student id.
      return true;
    };

    const matchesClass = (m: any) => {
      if (!classId || classId <= 0) return true;
      if (m?.classId == null) return true;
      return Number(m.classId) === classId;
    };

    const matchesSessionStrict = (m: any) => {
      if (!sessionId || sessionId <= 0) return true;
      if (m?.sessionId != null) return Number(m.sessionId) === sessionId;
      if (sessionName && m?.sessionYear != null) return String(m.sessionYear).trim() === sessionName;
      return false;
    };

    const matchesSessionLoose = (m: any) => {
      if (!sessionId || sessionId <= 0) return true;
      if (m?.sessionId != null && Number(m.sessionId) === sessionId) return true;
      if (sessionName && m?.sessionYear != null && String(m.sessionYear).trim() === sessionName) return true;
      return false;
    };

    const scoreType = (m: any) => (typeMatchesTcSelection(m?.marksheetType, wantType) ? 1 : 0);

    const pickBest = (cands: any[]) => {
      if (!cands.length) return undefined;
      const ranked = cands
        .map((m) => ({ m, s: scoreType(m) }))
        .sort((a, b) => b.s - a.s);
      const best = ranked.find((x) => x.s > 0)?.m ?? ranked[0]!.m;
      const id = Number(best?.id);
      return Number.isFinite(id) && id > 0 ? id : undefined;
    };

    const base = list.filter(matchesStudent);
    const a = base.filter(matchesClass).filter(matchesSessionStrict);
    const idA = pickBest(a);
    if (idA) return idA;

    const b = base.filter(matchesClass).filter(matchesSessionLoose);
    const idB = pickBest(b);
    if (idB) return idB;

    const c = base.filter(matchesClass);
    const idC = pickBest(c);
    if (idC) return idC;

    return pickBest(base);
  };

  // Helper function to remove section from class name (e.g., "10th A" -> "10th", "10th - A" -> "10th")
  const removeSection = (className: string): string => {
    if (!className) return "";
    // Extract just the class number and "th" part (e.g., "10th A" -> "10th", "10th - A" -> "10th")
    const match = className.match(/^(\d+th)/i);
    if (match) {
      return match[1];
    }
    // Fallback: take first word (should be the class name)
    const firstWord = className.trim().split(/\s+/)[0];
    return firstWord || className;
  };

  // Generate next class dropdown options based on result class
  const nextClassOptions = useMemo(() => {
    if (!editFormValues?.resultClass) return [];
    
    const resultClass = editFormValues.resultClass;
    const resultClassRank = getClassRank(resultClass);
    
    if (resultClassRank === -100) return [];
    
    // Get the result class base name (without section)
    const resultClassBase = removeSection(resultClass);
    const options: string[] = [];
    
    // Always add the result class (without section)
    if (resultClassBase) {
      options.push(resultClassBase);
    }
    
    // Get the next class number
    const nextClassRank = resultClassRank + 1;
    
    // Always generate the next class name (e.g., 7 -> "8th", 10 -> "11th")
    // Try to find it in classesQuery.data first, otherwise generate it
    let nextClassBase = "";
    
    if (classesQuery.data && classesQuery.data.length > 0) {
      // Find a class with the next class number
      const nextClass = (classesQuery.data || []).find((cls: any) => {
        const clsRank = getClassRank(cls.name || "");
        return clsRank === nextClassRank;
      });
      
      if (nextClass) {
        nextClassBase = removeSection(nextClass.name || "");
      }
    }
    
    // If not found in classes list, generate it (e.g., 7 -> "8th", 10 -> "11th")
    if (!nextClassBase && nextClassRank >= 3 && nextClassRank <= 15) {
      // Index 3 corresponds to class 1st
      const numericPart = nextClassRank - 2;
      nextClassBase = `${numericPart}th`;
    }
    
    // Add next class if it exists and is not already in options
    if (nextClassBase && !options.includes(nextClassBase)) {
      options.push(nextClassBase);
    }
    
    return options;
  }, [editFormValues?.resultClass, classesQuery.data]);

  return (
    <>
      <Paper sx={{ p: 1.5, maxWidth: 1500, mx: "auto", mt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                fontSize: "1.25rem",
                whiteSpace: "nowrap",
              }}
            >
              {isStudentUser ? t("transfer_certificate") : t("tc_list")}
            </Typography>
            {marksheetType && !isStudentUser && (
              <Typography 
                variant="body2" 
                sx={{ 
                  px: 1.5, 
                  py: 0.25, 
                  backgroundColor: "#e3f2fd", 
                  color: "#1976d2",
                  borderRadius: 1,
                  fontWeight: 600,
                  fontSize: '0.75rem'
                }}
              >
                {t(marksheetType.toLowerCase())}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {!isStudentUser && (
              <TextField
                size="small"
                placeholder={t("search")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 180, '& .MuiInputBase-root': { height: 36 } }}
              />
            )}
            {isStudentUser ? (
              <Button
                variant="contained"
                size="small"
                sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, height: 36 }}
                startIcon={<Description />}
                onClick={handleGetTcForStudent}
                disabled={!selectedClass || !selectedSession || !studentCode || loadingStudentTc}
              >
                {loadingStudentTc ? t("loading") : t("get_tc")}
              </Button>
            ) : (
              <Button
                variant="contained"
                size="small"
                sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, height: 36 }}
                startIcon={<Description />}
                onClick={handleOpenDialog}
                disabled={!user?.schoolId || !selectedClass || !selectedSession || !marksheetType}
              >
                {t("get_tc_list")}
              </Button>
            )}
          </Box>
        </Box>

      {!isStudentUser && (
        <EnhancedTable
          columns={tableColumns}
          data={formattedRows}
          searchFields={["studentName", "className", "sessionYear", "result"]}
          hideSearch
          externalSearchTerm={searchTerm}
          cardProps={{
            elevation: 0,
            sx: {
              border: 'none',
              boxShadow: 'none',
              backgroundColor: 'transparent',
              mb: 0,
              '& .MuiCardContent-root': { p: 0, '&:last-child': { pb: 0 } },
            },
          }}
          actions={(row: any) => (
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Button
              variant="contained"
                size="small"
                sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
                onClick={(e) => { e.stopPropagation(); handleOpenTcForStudent(row.__tcStudent, row.marksheetId); }}
              >
                {t("view")}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={(e) => { 
                  e.stopPropagation();
                  const tcRow = row.__tcStudent || {};
                  const studentId = Number(tcRow.studentId ?? tcRow.student?.id ?? row.studentId);
                  // Find existing TC data from backend (use pre-matched data from row if available)
                  const marksheetId =
                    row.marksheetId ??
                    tcRow.marksheetId ??
                    tcRow.marksheetID ??
                    tcRow.marksheet?.id ??
                    resolveMarksheetId(Number.isFinite(studentId) ? studentId : undefined);
                  let existingTcData = row.__tcData || null;
                  
                  // Fallback: search in tcDataList if not found in row
                  if (!existingTcData && marksheetId && Array.isArray(tcDataList)) {
                    existingTcData = tcDataList.find((tc: any) => 
                      tc.marksheet && Number(tc.marksheet.id) === Number(marksheetId)
                    ) || null;
                  }
                  
                  // Helper function to convert dd/MM/yyyy to yyyy-mm-dd for DatePicker
                  const toYyyyMmDd = (dateStr: string | null | undefined): string => {
                    if (!dateStr) return "";
                    // If already in yyyy-mm-dd format, return as is
                    if (dateStr.includes("-") && dateStr.length === 10) {
                      return dateStr;
                    }
                    // If in dd/MM/yyyy format, convert to yyyy-mm-dd
                    if (dateStr.includes("/")) {
                      const parts = dateStr.split("/");
                      if (parts.length === 3) {
                        const [d, m, y] = parts;
                        return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
                      }
                    }
                    // Try parsing as Date
                    const d = new Date(dateStr);
                    if (!Number.isNaN(d.getTime())) {
                      const y = d.getFullYear();
                      const m = String(d.getMonth() + 1).padStart(2, "0");
                      const day = String(d.getDate()).padStart(2, "0");
                      return `${y}-${m}-${day}`;
                    }
                    return "";
                  };
                  
                  // Prepare form values with existing data if available
                  const defaults = {
                    studentId: Number.isFinite(studentId) ? studentId : row.studentId,
                    marksheetId: marksheetId,
                    studentName: row.studentName,
                    resultClass: row.className,
                    result: row.result,
                    nextClass: "",
                    yearOfResult: row.sessionYear,
                    dateOfLeaving: "",
                    totalDays: "",
                    presentDays: "",
                    behaviour: "",
                    nextSchoolDetails: "",
                    nextSchoolStatus: "Other",
                  };
                  
                  // Merge with existing TC data if found
                  const formValues = existingTcData ? {
                    ...defaults,
                    resultClass: existingTcData.resultClass || defaults.resultClass,
                    result: existingTcData.result || defaults.result,
                    nextClass: existingTcData.nextClass || "",
                    yearOfResult: existingTcData.yearOfResult || defaults.yearOfResult,
                    dateOfLeaving: toYyyyMmDd(existingTcData.dateOfLeaving),
                    totalDays: existingTcData.totalDays?.toString() || "",
                    presentDays: existingTcData.presentDays?.toString() || "",
                    behaviour: existingTcData.behaviour || "",
                    nextSchoolDetails: existingTcData.nextSchoolDetails || "",
                    nextSchoolStatus: existingTcData.nextSchoolStatus || "Other",
                  } : defaults;
                  
                  setEditFormValues(formValues);
                  setEditModalOpen(true);
                }}
              >
                {t("edit")}
            </Button>
          </Box>
          )}
        />
      )}

      {isStudentUser ? (
        (!selectedClass || !selectedSession || !studentCode) && (
          <Alert severity="info" sx={{ mt: 1 }}>
            {t("please_select_a_class_and_session_to_view_your_tc")}
          </Alert>
        )
      ) : (
        (!user?.schoolId ||
        !selectedClass ||
        !selectedSession ||
        !marksheetType) && (
          <Alert severity="info" sx={{ mt: 1 }}>
            {t("please_select_a_class_session_and_marksheet_type_to_view_the_tc_list")}
          </Alert>
        )
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>{error.message}</Alert>
      )}

      </Paper>

      {/* Dialog for TC List Template */}
      <CommonModal
        open={dialogOpen}
        onClose={handleCloseDialog}
        title={`${selectedClass?.name ?? ""} ${t("tc_list")} - ${selectedExamType}`}
        maxWidth="lg"
        fullWidth
        hideActions
        PaperProps={{ sx: { maxHeight: "90vh" } }}
        contentSx={{ p: 2, overflow: "auto" }}
      >
        {tcList && tcList.length > 0 ? (
          <TcListTemplate
            tcListData={tcList}
            schoolName={currentSchool?.nameofSchool || "School Name"}
            district={currentSchool?.district || "District"}
            block={currentSchool?.blockName || "Block"}
            diseCode={currentSchool?.diseCode || "DISE Code"}
            mpbseCode={currentSchool?.schoolCode || "School Code"}
            className={selectedClass?.name || ""}
            examType={selectedExamType}
            sessionYear={selectedSession?.name || ""}
            hidePrintButton={false}
          />
        ) : (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h6" color="text.secondary">
              No TC list data available
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Please check if there are students with TC data for the selected
              exam type.
            </Typography>
          </Box>
        )}
      </CommonModal>

      {/* Removed old fetch dialog in favor of per-row actions */}

      {/* Dialog for Displaying TC Template */}
      <CommonModal
        open={tcDisplayDialogOpen}
        onClose={handleCloseTcDisplayDialog}
        title={`Transfer Certificate - ${selectedStudent?.studentName || "Student"}`}
        maxWidth="xl"
        fullWidth
        hideActions
        PaperProps={{
          sx: {
            maxHeight: "95vh",
            width: { xs: "calc(100vw - 32px)", sm: "75vw", md: "60vw" },
            maxWidth: { xs: "calc(100vw - 32px)", md: "none" },
          },
        }}
        contentSx={{ p: 2, overflow: "auto" }}
      >
        {selectedStudent && (() => {
          // For student users, use the fetched TC data directly
          if (isStudentUser && studentTcData) {
            return (
              <TcTemplate
                student={selectedStudent}
                ref={tcTemplateRef}
                tcData={studentTcData}
              />
            );
          }

          // For admin users, use existing logic
          const studentId = selectedStudent?.studentId || selectedStudent?.id || selectedStudent?.student?.id;

          const marksheetId = selectedStudent?.__marksheetId ||
            formattedRows.find((row: any) => row.studentId === studentId)?.marksheetId ||
            resolveMarksheetId(studentId);

          const rowWithData = formattedRows.find(
            (row: any) => row.studentId === studentId ||
                         (marksheetId && Number(row.marksheetId) === Number(marksheetId))
          );

          let matchingTcData = rowWithData?.__tcData;

          if (!matchingTcData && marksheetId && Array.isArray(tcDataList)) {
            matchingTcData = tcDataList.find((tc: any) =>
              tc.marksheet && Number(tc.marksheet.id) === Number(marksheetId)
            );
          }

          if (!matchingTcData && studentId && Array.isArray(tcDataList)) {
            matchingTcData = tcDataList.find((tc: any) =>
              tc.marksheet?.student?.id && Number(tc.marksheet.student.id) === Number(studentId)
            );
          }

          return (
            <TcTemplate
              student={selectedStudent}
              ref={tcTemplateRef}
              tcData={matchingTcData || null}
            />
          );
        })()}
      </CommonModal>

      {/* Edit TC Data Modal - Only show for admin users */}
      {!isStudentUser && (
      <CommonModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title={t("edit_tc_data")}
        maxWidth="sm"
        fullWidth
        customActions={
          <>
            <Button
              onClick={() => setEditModalOpen(false)}
              variant="outlined"
              sx={{ borderColor: "#3092e3", color: "#3092e3", "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" } }}
            >
              {t("cancel")}
            </Button>
            <Button
              variant="contained"
              sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
              disabled={saving}
              onClick={async () => {
                if (!editFormValues) return;
                const sid = Number(editFormValues.studentId);
                let marksheetId =
                  editFormValues.marksheetId ||
                  resolveMarksheetId(Number.isFinite(sid) ? sid : undefined);
                if (marksheetId === undefined || marksheetId === null || Number.isNaN(Number(marksheetId))) {
                  marksheetId = resolveMarksheetId(Number.isFinite(sid) ? sid : undefined);
                }
                if (marksheetId === undefined || marksheetId === null || Number.isNaN(Number(marksheetId))) {
                  try {
                    marksheetId = await resolveMarksheetIdFromServer(Number.isFinite(sid) ? sid : undefined);
                  } catch (e: any) {
                    showError(
                      e?.response?.data?.message ||
                        e?.message ||
                        t("failed_to_fetch_tc_data") ||
                        "Failed to resolve marksheet id"
                    );
                    return;
                  }
                }
                if (marksheetId === undefined || marksheetId === null || Number.isNaN(Number(marksheetId))) {
                  showError(
                    "Marksheet ID not found. Please ensure marksheet is available for this student."
                  );
                  return;
                }
                try {
                  setSaving(true);
                  const toDdMmYyyy = (value?: string): string => {
                    if (!value) return "";
                    if (value.includes("/")) return value;
                    const parts = value.split("-");
                    if (parts.length === 3) {
                      const [y, m, d] = parts;
                      return `${d}/${m}/${y}`;
                    }
                    const d = new Date(value);
                    if (!Number.isNaN(d.getTime())) {
                      const dd = String(d.getDate()).padStart(2, "0");
                      const mm = String(d.getMonth() + 1).padStart(2, "0");
                      const yy = d.getFullYear();
                      return `${dd}/${mm}/${yy}`;
                    }
                    return value;
                  };
                  const payload = {
                    marksheetId: Number(marksheetId),
                    classId: selectedClass?.id ? Number(selectedClass.id) : 0,
                    sessionId: selectedSession?.id ? Number(selectedSession.id) : 0,
                    resultClass: editFormValues.resultClass || "",
                    result: editFormValues.result || "",
                    nextClass: editFormValues.nextClass || "",
                    yearOfResult: editFormValues.yearOfResult || "",
                    dateOfLeaving: toDdMmYyyy(editFormValues.dateOfLeaving) || "",
                    totalDays: editFormValues.totalDays ? Number(editFormValues.totalDays) : 0,
                    presentDays: editFormValues.presentDays ? Number(editFormValues.presentDays) : 0,
                    behaviour: editFormValues.behaviour || "",
                    nextSchoolDetails: editFormValues.nextSchoolDetails || "",
                    nextSchoolStatus: editFormValues.nextSchoolStatus || "",
                  } as any;
                  await addTcDataMutation.mutateAsync(payload);
                  cacheTcData();
                  refetchTcData();
                  setEditModalOpen(false);
                  showSuccess(t('tc_updated_successfully') || 'TC updated successfully');
                } catch (e: any) {
                  const errorMessage = e?.response?.data?.message || e?.message || t('error_updating_tc') || 'Failed to update TC. Please try again.';
                  showError(errorMessage);
                } finally {
                  setSaving(false);
                }
              }}
            >
              {t("save")}
            </Button>
          </>
        }
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={1.2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("student_name")}
                  value={editFormValues?.studentName || ""}
                  margin="dense"
                  size="small"
                  variant="outlined"
                  InputProps={{ readOnly: true }}
                  sx={{ backgroundColor: "#f8f9fb" }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("result_class")}
                  value={editFormValues?.resultClass || ""}
                  margin="dense"
                  size="small"
                  variant="outlined"
                  InputProps={{ readOnly: true }}
                  sx={{ backgroundColor: "#f8f9fb" }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("result")}
                  value={editFormValues?.result || ""}
                  margin="dense"
                  size="small"
                  variant="outlined"
                  InputProps={{ readOnly: true }}
                  sx={{ backgroundColor: "#f8f9fb" }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("year_of_result")}
                  value={editFormValues?.yearOfResult || ""}
                  margin="dense"
                  size="small"
                  variant="outlined"
                  InputProps={{ readOnly: true }}
                  sx={{ backgroundColor: "#f8f9fb" }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label={t("next_class")}
                  value={editFormValues?.nextClass || ""}
                  onChange={(e) => setEditFormValues((p: any) => ({ ...p, nextClass: e.target.value }))}
                  margin="dense"
                  size="small"
                  variant="outlined"
                  sx={{ backgroundColor: "#f8f9fb" }}
                >
                  {nextClassOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label={t("date_of_leaving") as string}
                  value={editFormValues?.dateOfLeaving ? new Date(editFormValues.dateOfLeaving) : null}
                  onChange={(date) => {
                    if (date && !isNaN(date.getTime())) {
                      const y = date.getFullYear();
                      const m = String(date.getMonth() + 1).padStart(2, "0");
                      const d = String(date.getDate()).padStart(2, "0");
                      setEditFormValues((p: any) => ({ ...p, dateOfLeaving: `${y}-${m}-${d}` }));
                    } else {
                      setEditFormValues((p: any) => ({ ...p, dateOfLeaving: "" }));
                    }
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      margin: "dense",
                      size: "small",
                      variant: "outlined",
                      sx: { backgroundColor: "#f8f9fb", width: "100%" },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("total_days")}
                  type="number"
                  value={editFormValues?.totalDays || ""}
                  onChange={(e) => setEditFormValues((p: any) => ({ ...p, totalDays: e.target.value }))}
                  margin="dense"
                  size="small"
                  variant="outlined"
                  sx={{ backgroundColor: "#f8f9fb" }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("present_days")}
                  type="number"
                  value={editFormValues?.presentDays || ""}
                  onChange={(e) => setEditFormValues((p: any) => ({ ...p, presentDays: e.target.value }))}
                  margin="dense"
                  size="small"
                  variant="outlined"
                  sx={{ backgroundColor: "#f8f9fb" }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("behaviour")}
                  value={editFormValues?.behaviour || ""}
                  onChange={(e) => setEditFormValues((p: any) => ({ ...p, behaviour: e.target.value }))}
                  margin="dense"
                  size="small"
                  variant="outlined"
                  sx={{ backgroundColor: "#f8f9fb" }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("next_school_name")}
                  value={editFormValues?.nextSchoolDetails || ""}
                  onChange={(e) => setEditFormValues((p: any) => ({ ...p, nextSchoolDetails: e.target.value }))}
                  margin="dense"
                  size="small"
                  variant="outlined"
                  sx={{ backgroundColor: "#f8f9fb" }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label={t("next_school_status")}
                  value={editFormValues?.nextSchoolStatus || "Other"}
                  onChange={(e) => setEditFormValues((p: any) => ({ ...p, nextSchoolStatus: e.target.value }))}
                  margin="dense"
                  size="small"
                  variant="outlined"
                  sx={{ backgroundColor: "#f8f9fb" }}
                >
                  <MenuItem value="Other">Other</MenuItem>
                  <MenuItem value="Same">Same</MenuItem>
                  <MenuItem value="Nil">Nil</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </LocalizationProvider>
      </CommonModal>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TcManagement;